import React from 'react';
import { X } from 'lucide-react';
import { CollectibleObject } from '../types';
import { useAppData } from '../hooks/useAppData';

interface RewardModalProps {
  onClose: () => void;
  rewards: CollectibleObject[];
  bookTitle: string;
  isReplay?: boolean; // Indique si c'est une relecture (déjà complété)
  syncConflict?: boolean; // Indique si complété sur un autre appareil pendant offline
}

const RewardModal: React.FC<RewardModalProps> = ({ onClose, rewards, bookTitle, isReplay = false, syncConflict = false }) => {
  const { readingTexts } = useAppData();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          <div className="text-center">
            <div className="text-6xl mb-3 animate-bounce">🎉</div>
            <h2 className="text-2xl font-bold">{readingTexts.theEnd}</h2>
            <p className="text-sm opacity-90 mt-1">{bookTitle}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {rewards.length > 0 ? (
            <>
              <h3 className="text-xl font-semibold text-center mb-6 text-gray-800">
                {readingTexts.rewardsUnlocked}
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {rewards.map((reward, index) => (
                  <div
                    key={reward.id}
                    className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 flex flex-col items-center justify-center hover:scale-105 transition-transform animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="w-20 h-20 mb-2 flex items-center justify-center">
                      {reward.emoji ? (
                        <img
                          src={reward.emoji}
                          alt={reward.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="text-4xl">✨</div>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-800 text-center">
                      {reward.name}
                    </p>
                    {reward.isSpecial && (
                      <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full mt-1">
                        ⭐ Spécial
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-sm text-blue-800">
                  💡 Retrouve tes objets dans la collection !
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">{syncConflict ? '📱' : isReplay ? '📚' : '📖'}</div>
              <p className="text-gray-600">
                {syncConflict
                  ? 'Cette histoire a déjà été terminée sur un autre appareil. Les récompenses ont été obtenues lors de cette première lecture.'
                  : isReplay
                  ? 'Tu as déjà lu cette histoire ! Les récompenses ne sont données qu\'à la première lecture.'
                  : 'Histoire terminée ! Aucune récompense pour cette histoire.'}
              </p>
              {syncConflict && (
                <p className="text-sm text-blue-600 mt-2">
                  🔄 Tes récompenses sont synchronisées depuis l'autre appareil.
                </p>
              )}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-full mt-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all hover:scale-105 shadow-lg"
          >
            Continuer
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default RewardModal;
